import { MAX_VOUCHER_TRANSPORT_LEGS, TRANSPORT_LEG_MODES } from '@gty/common';
import type {
  TransportLegMode,
  UpdateVoucherTransportLegsDto,
  UpsertVoucherTransportLegDto,
  VoucherTransportLegDto,
  WallClockDateTime,
} from '@gty/common';

import { Field, Select, TextInput } from '../ui/Modal';
import { Button } from '../ui/primitives';
import { WallClockPicker } from './WallClockPicker';

/**
 * The "Transport" sub-form the voucher editor mounts.
 *
 * ## WHAT IT EDITS
 *
 * The voucher's OWN transport legs — flights, trains, coaches — typed in by
 * staff off the tickets. Unlike the passenger and vendor sub-forms these rows
 * are not a snapshot of anything, so the SET is editable: a leg can be added,
 * corrected and removed. Document-only: nothing here reaches the booking or the
 * money.
 *
 * ## THE PATCH IS ID-KEYED, NOT A WHOLE-LIST REPLACE
 *
 * `diffTransportLegs` turns the drafts into `{ upsert, removeIds }`: a draft
 * with no `id` is a new leg, a draft with an `id` whose fields changed is a
 * correction to that leg, and a server leg missing from the drafts was removed
 * by the user and goes in `removeIds`. Unchanged legs are omitted entirely —
 * the same "only send what changed" rule as every other field in the editor.
 *
 * ## THE TIMES ARE NAIVE WALL-CLOCK STRINGS — NO `Date` ANYWHERE
 *
 * `departureAt`/`arrivalAt` are `WallClockDateTime` (`YYYY-MM-DDTHH:mm`, no
 * zone), which is EXACTLY the value an `<input type="datetime-local">` reads
 * and writes. The input's own `.value` is bound straight to the draft and the
 * draft straight to the DTO — never through `new Date(...)`/`toISOString()`,
 * which would apply the browser's UTC offset and shift every printed time. The
 * arrival-not-before-departure check compares the strings themselves: in this
 * fixed-width format lexicographic order IS chronological order.
 */

/** Matches the server's `@MaxLength` on every free-text leg field. */
const TEXT_MAX_LENGTH = 120;

/**
 * The stored enum value -> the label staff and the traveller read.
 *
 * Shared with the printed voucher, the same way `VOUCHER_VENDOR_KIND_LABEL` is.
 */
export const TRANSPORT_LEG_MODE_LABEL: Readonly<Record<TransportLegMode, string>> = {
  FLIGHT: 'Flight',
  TRAIN: 'Train',
  BUS: 'Bus',
  CAR: 'Car',
  FERRY: 'Ferry',
  OTHER: 'Other',
};

/** Generic fallback column/field label for a mode with no specific one below. */
export const CARRIER_OR_NUMBER_FALLBACK_LABEL = 'Carrier / No.';

/**
 * The carrier/number field's label follows the leg's own mode — a flight's
 * number reads differently from a train's or a cab's registration plate.
 * Shared by the edit form and the printed voucher so they can never drift.
 *
 * FERRY has no mode-specific wording in the product ask and falls through to
 * the generic label, same as OTHER.
 */
export const TRANSPORT_LEG_CARRIER_LABEL: Readonly<Record<TransportLegMode, string>> = {
  FLIGHT: 'Flight No.',
  TRAIN: 'Train No.',
  BUS: 'Bus No.',
  CAR: 'Vehicle No.',
  FERRY: CARRIER_OR_NUMBER_FALLBACK_LABEL,
  OTHER: CARRIER_OR_NUMBER_FALLBACK_LABEL,
};

/** A realistic example per mode, so the placeholder reads as a hint, not noise. */
export const TRANSPORT_LEG_CARRIER_PLACEHOLDER: Readonly<Record<TransportLegMode, string>> = {
  FLIGHT: 'GA 845',
  TRAIN: '19411 / Name',
  BUS: 'VRL 2234',
  CAR: 'MH-12 AB 1234',
  FERRY: 'GA 845',
  OTHER: 'GA 845',
};

/**
 * One leg as FORM STRINGS.
 *
 * `id` is the server's id, or `null` for a leg added in this form and not yet
 * saved. `key` is the React key only — stable for a new row that has no id —
 * and is never sent. `arrivalAt` is `''` while unknown; it becomes `null` in
 * the patch.
 */
export interface TransportLegDraft {
  key: string;
  id: string | null;
  mode: TransportLegMode;
  carrierOrNumber: string;
  fromPlace: string;
  toPlace: string;
  departureAt: WallClockDateTime;
  arrivalAt: WallClockDateTime;
  seatOrPnr: string;
}

export function toTransportLegDrafts(
  rows: readonly VoucherTransportLegDto[],
): TransportLegDraft[] {
  return rows.map((leg) => ({
    key: leg.id,
    id: leg.id,
    mode: leg.mode,
    carrierOrNumber: leg.carrierOrNumber,
    fromPlace: leg.fromPlace,
    toPlace: leg.toPlace,
    departureAt: leg.departureAt,
    arrivalAt: leg.arrivalAt ?? '',
    seatOrPnr: leg.seatOrPnr,
  }));
}

let nextNewLegKey = 0;

/** A blank row for "Add transport leg". No `id` — the server assigns one on save. */
export function blankTransportLegDraft(): TransportLegDraft {
  nextNewLegKey += 1;
  return {
    key: `new-leg-${nextNewLegKey}`,
    id: null,
    mode: 'FLIGHT',
    carrierOrNumber: '',
    fromPlace: '',
    toPlace: '',
    departureAt: '',
    arrivalAt: '',
    seatOrPnr: '',
  };
}

/**
 * Why ONE row cannot be saved yet, or `null` when it can.
 *
 * Mirrors the server: from, to and departure are required, and arrival — when
 * given — may not be before departure. The comparison is on the raw strings;
 * see the module note for why that is both correct and deliberate.
 */
export function transportLegProblem(leg: TransportLegDraft): string | null {
  if (leg.fromPlace.trim() === '' || leg.toPlace.trim() === '' || leg.departureAt === '') {
    return 'Fill in From, To and Departure to save this leg.';
  }
  if (leg.arrivalAt !== '' && leg.arrivalAt < leg.departureAt) {
    return 'Arrival cannot be before departure.';
  }
  return null;
}

/**
 * Why the WHOLE set cannot be saved, or `null` when it can.
 *
 * The cap is enforced by the server under a row lock; this is only so the
 * editor explains itself instead of round-tripping for a 400.
 */
export function transportLegsBlockReason(drafts: readonly TransportLegDraft[]): string | null {
  if (drafts.length > MAX_VOUCHER_TRANSPORT_LEGS) {
    return `A voucher can carry at most ${MAX_VOUCHER_TRANSPORT_LEGS} transport legs — remove ${
      drafts.length - MAX_VOUCHER_TRANSPORT_LEGS
    } to save.`;
  }
  const firstBad = drafts.findIndex((leg) => transportLegProblem(leg) !== null);
  if (firstBad >= 0) {
    return `Transport leg ${firstBad + 1}: ${transportLegProblem(drafts[firstBad] as TransportLegDraft) ?? ''}`;
  }
  return null;
}

/** The draft row as the wire shape. `''` arrival is an explicit `null` (unknown / cleared). */
function toUpsert(leg: TransportLegDraft): UpsertVoucherTransportLegDto {
  return {
    ...(leg.id !== null ? { id: leg.id } : {}),
    mode: leg.mode,
    carrierOrNumber: leg.carrierOrNumber,
    fromPlace: leg.fromPlace,
    toPlace: leg.toPlace,
    // Verbatim — the datetime-local value, never converted through `Date`.
    departureAt: leg.departureAt,
    arrivalAt: leg.arrivalAt === '' ? null : leg.arrivalAt,
    seatOrPnr: leg.seatOrPnr,
  };
}

function legChanged(original: VoucherTransportLegDto, draft: TransportLegDraft): boolean {
  return (
    draft.mode !== original.mode ||
    draft.carrierOrNumber !== original.carrierOrNumber ||
    draft.fromPlace !== original.fromPlace ||
    draft.toPlace !== original.toPlace ||
    draft.departureAt !== original.departureAt ||
    (draft.arrivalAt === '' ? null : draft.arrivalAt) !== original.arrivalAt ||
    draft.seatOrPnr !== original.seatOrPnr
  );
}

/**
 * ONLY WHAT CHANGED, as an id-keyed upsert plus explicit removals.
 *
 * Exported for its own test. Same convention as `diffPassengers` and
 * `diffVendorNotes`, widened for a row set that can grow and shrink:
 *
 * - a draft with no `id` is a new leg -> `upsert` without an id;
 * - a draft with an `id` whose fields differ from the server -> `upsert` with it;
 * - a server leg with no matching draft was removed -> `removeIds`;
 * - an unchanged leg is omitted entirely.
 *
 * Returns `null` when there is nothing to send, so the caller can leave the key
 * off the patch and a save that touched no leg records no leg change.
 */
export function diffTransportLegs(
  original: readonly VoucherTransportLegDto[],
  drafts: readonly TransportLegDraft[],
): UpdateVoucherTransportLegsDto | null {
  const byId = new Map(original.map((leg) => [leg.id, leg]));
  const upsert: UpsertVoucherTransportLegDto[] = [];
  const kept = new Set<string>();

  for (const draft of drafts) {
    if (draft.id === null) {
      upsert.push(toUpsert(draft));
      continue;
    }
    const leg = byId.get(draft.id);
    // An id this voucher no longer holds: sending it would be a 400. The
    // editor re-seeds its legs whenever the server's set changes, so this is
    // only reachable mid-refetch; skip rather than send a doomed correction.
    if (leg === undefined) continue;
    kept.add(draft.id);
    if (legChanged(leg, draft)) upsert.push(toUpsert(draft));
  }

  const removeIds = original.filter((leg) => !kept.has(leg.id)).map((leg) => leg.id);

  if (upsert.length === 0 && removeIds.length === 0) return null;
  return {
    ...(upsert.length > 0 ? { upsert } : {}),
    ...(removeIds.length > 0 ? { removeIds } : {}),
  };
}

/**
 * The rendered sub-form.
 *
 * ALWAYS rendered, even with no legs — unlike "Stay / Services" this section
 * has an add button, and staff need it to enter the first leg.
 *
 * `disabled` is inherited from the editor's enclosing `<fieldset>` (which also
 * disables the add/remove buttons), so a lock or a view-only role greys the
 * whole section without this component knowing about permissions.
 */
export function VoucherTransportLegFields({
  drafts,
  onChange,
  onAdd,
  onRemove,
}: {
  drafts: readonly TransportLegDraft[];
  onChange: (key: string, changes: Partial<TransportLegDraft>) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
}): JSX.Element {
  const atCap = drafts.length >= MAX_VOUCHER_TRANSPORT_LEGS;

  return (
    <div className="border-t border-neutral-200 px-5 py-4">
      <h3 className="text-[11px] font-semibold uppercase leading-none tracking-caps text-neutral-600">
        Transport
      </h3>
      <p className="mt-1.5 text-[11.5px] leading-[1.5] text-neutral-600">
        Flights, trains and coaches as printed on the voucher. Times are entered exactly as on the
        ticket. Up to {MAX_VOUCHER_TRANSPORT_LEGS} legs.
      </p>

      {drafts.length > 0 && (
        <div className="mt-3 grid gap-4">
          {drafts.map((leg, index) => {
            const n = index + 1;
            const problem = transportLegProblem(leg);
            const arrivalBeforeDeparture =
              leg.arrivalAt !== '' && leg.departureAt !== '' && leg.arrivalAt < leg.departureAt;
            return (
              <div
                key={leg.key}
                className="grid gap-3 rounded-[8px] border border-neutral-300 p-3.5 sm:grid-cols-2"
              >
                {/*
                  Labelled per row ("Leg 1 from") rather than by a column
                  header, so a screen reader and a keyboard user both know which
                  leg they are editing.
                */}
                <Field label={`Leg ${n} mode`}>
                  <Select
                    value={leg.mode}
                    onChange={(e) => {
                      onChange(leg.key, { mode: e.target.value as TransportLegMode });
                    }}
                  >
                    {TRANSPORT_LEG_MODES.map((mode) => (
                      <option key={mode} value={mode}>
                        {TRANSPORT_LEG_MODE_LABEL[mode]}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label={`Leg ${n} ${TRANSPORT_LEG_CARRIER_LABEL[leg.mode].toLowerCase()}`}>
                  <TextInput
                    value={leg.carrierOrNumber}
                    maxLength={TEXT_MAX_LENGTH}
                    placeholder={TRANSPORT_LEG_CARRIER_PLACEHOLDER[leg.mode]}
                    onChange={(e) => {
                      onChange(leg.key, { carrierOrNumber: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Leg ${n} from`} required>
                  <TextInput
                    value={leg.fromPlace}
                    maxLength={TEXT_MAX_LENGTH}
                    placeholder="Mumbai (BOM)"
                    onChange={(e) => {
                      onChange(leg.key, { fromPlace: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Leg ${n} to`} required>
                  <TextInput
                    value={leg.toPlace}
                    maxLength={TEXT_MAX_LENGTH}
                    placeholder="Denpasar (DPS)"
                    onChange={(e) => {
                      onChange(leg.key, { toPlace: e.target.value });
                    }}
                  />
                </Field>

                {/*
                  `WallClockPicker` produces and consumes the same wire format
                  (`YYYY-MM-DDTHH:mm`, no zone) the native picker did — bound
                  straight through, no `Date` round-trip. Swapped off the
                  browser's own `datetime-local` widget because that renders in
                  whatever 12-/24-hour convention the browser's locale uses,
                  not the explicit AM/PM this product wants shown regardless of
                  locale.
                */}
                <Field label={`Leg ${n} departure`} required>
                  <WallClockPicker
                    required
                    label={`Leg ${n} departure`}
                    value={leg.departureAt}
                    onChange={(next) => {
                      onChange(leg.key, { departureAt: next });
                    }}
                  />
                </Field>

                <Field
                  label={`Leg ${n} arrival`}
                  hint="Optional — leave empty when unknown."
                  error={arrivalBeforeDeparture ? 'Arrival cannot be before departure.' : undefined}
                >
                  <WallClockPicker
                    label={`Leg ${n} arrival`}
                    value={leg.arrivalAt}
                    invalid={arrivalBeforeDeparture}
                    onChange={(next) => {
                      onChange(leg.key, { arrivalAt: next });
                    }}
                  />
                </Field>

                <Field label={`Leg ${n} seat / PNR`} hint="Optional.">
                  <TextInput
                    value={leg.seatOrPnr}
                    maxLength={TEXT_MAX_LENGTH}
                    onChange={(e) => {
                      onChange(leg.key, { seatOrPnr: e.target.value });
                    }}
                  />
                </Field>

                <div className="flex flex-wrap items-end justify-between gap-3">
                  {problem !== null && !arrivalBeforeDeparture ? (
                    <span className="text-[11px] leading-[1.4] text-neutral-600">{problem}</span>
                  ) : (
                    <span />
                  )}
                  <Button
                    variant="ghost"
                    aria-label={`Remove leg ${n}`}
                    onClick={() => {
                      onRemove(leg.key);
                    }}
                  >
                    Remove leg
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="outline" disabled={atCap} onClick={onAdd}>
          Add transport leg
        </Button>
        {atCap && (
          <span className="text-[11px] leading-[1.4] text-neutral-600">
            Maximum of {MAX_VOUCHER_TRANSPORT_LEGS} legs reached.
          </span>
        )}
      </div>
    </div>
  );
}
